import axios from "axios";

import constants from "../../constants";

/**
 * The agenda: its settings, its services and its bookings (contract: api-keepo-v2's AgendaController,
 * AgendaSettingsForm, AgendaServiceForm, AgendaBookingForm and AgendaBookings::view()). Set up on any Keepo (settings,
 * services, days off: PLUS is asked when publishing); without PLUS only the owner's own bookings (made in the editor)
 * answer 403 PLUS_REQUIRED.
 */

/** "0" (Sunday) to "6" => the day's intervals, in order: [["09:00", "12:00"], ["13:00", "18:00"]] */
export type AgendaWeek = Record<string, [string, string][]>;

export type AgendaSettings = {
  enabled: boolean;
  timezone: string;
  /** auto: a booking is confirmed at once; manual: it waits for the owner. */
  confirmation: "auto" | "manual";
  /** The owner's WhatsApp for the requests waiting for them (+<country><number>); null: by e-mail only. */
  notifyPhone: string | null;
  /** The contact form's WhatsApp (+<country><number>), which receives the requests until one is saved above. */
  formPhone?: string | null;
  /** The Keepo has its Agenda page published: without it only the owner books (in the editor). */
  pagePublished?: boolean;
  minNoticeMinutes: number;
  maxDaysAhead: number;
  bufferMinutes: number;
  /** Until how long before the time the customer cancels or reschedules by the link (0: any time). */
  cancelUntilMinutes: number;
  week: AgendaWeek;
  /** YYYY-MM-DD: whole days off */
  blockedDates: string[];
  /** YYYY-MM-DD => [HH:MM, HH:MM]: a part of a day off (one range per day) */
  blockedTimes: Record<string, [string, string]>;
  isPlus: boolean;
  /** This year's and next year's national holidays: YYYY-MM-DD => its key (named by the editor). */
  holidays?: Record<string, string>;
};

export type AgendaService = {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  priceCents: number | null;
  /** The price is the least ("a partir de"). */
  priceFrom: boolean;
  image: string | null;
  /** Shown on the Keepo's agenda page. */
  active: boolean;
  sort: number;
  /** Why this service can't be charged online (only with online payments on): none when it can. */
  onlineBlocker?: "no_price" | "price_from" | "below_minimum" | null;
};

export type AgendaServiceInput = Partial<Omit<AgendaService, "id" | "sort">>;

export type AgendaBookingStatus =
  | "pending"
  | "confirmed"
  | "declined"
  | "canceled"
  | "awaiting_payment"
  | "expired"
  | "paid_no_slot";

/** What the customer paid online, as the API shows it (only with online payments on). */
export type AgendaBookingPayment = {
  /** The sale's: pending, paid, failed, expired, refunded or chargeback. */
  status: string;
  /** pix or card (none until the customer picks one). */
  method: string | null;
  amountCents: number | null;
  /** The part of the amount that's the customer's fee. */
  feeCents: number | null;
  /** The hold on the time (the booking's). */
  expiresAt: string | null;
  pix: { qrCode: string; qrUrl: string; expiresAt: string | null } | null;
  failureReason: string | null;
  /** Each method's amount and fee (none when the sale has no quotes). */
  methods: Record<string, { amountCents: number; feeCents: number }> | null;
  /** The document comes masked. */
  buyer: { email: string; document: string | null };
  /** Only for the owner: what they get. */
  ownerNetCents?: number | null;
  /** Only for the owner: when it was refunded. */
  refundedAt?: string | null;
};

export type AgendaBooking = {
  id: string;
  status: AgendaBookingStatus;
  /** In the agenda's time zone: YYYY-MM-DD and HH:MM. */
  date: string;
  time: string;
  endTime: string;
  /** What was booked, as it was then. */
  service: { id: string | null; name: string; durationMinutes: number; priceCents: number | null; priceFrom: boolean };
  customer: { name: string; phone: string; email: string | null };
  /** The owner's fields, as filled. */
  fields: { label: string; type: string; value: string }[];
  createdBy: "customer" | "owner";
  /** A request to move another booking (its id): that one stays until this is confirmed. */
  replacesId: string | null;
  canChange: boolean;
  /** Only for a booking charged online. */
  payment?: AgendaBookingPayment | null;
};

export type AgendaBookingList = { items: AgendaBooking[]; totals: { confirmedCents: number } };

/** The owner's booking: any service, day and time (the agenda's rules don't apply), and the customer's data. */
export type AgendaBookingInput = { service: string; date: string; time: string; fields: Record<string, unknown> };

/** The API's error codes of the agenda. */
export const AGENDA_ERRORS = {
  notFound: "NOT_FOUND",
  plusRequired: "PLUS_REQUIRED",
  invalidInput: "INVALID_INPUT",
  serviceNotFound: "SERVICE_NOT_FOUND",
  formValidation: "FORM_VALIDATION_ERROR",
  notAllowed: "NOT_ALLOWED",
  notRefundable: "NOT_REFUNDABLE",
  insufficientBalance: "INSUFFICIENT_BALANCE",
  providerUnavailable: "PROVIDER_UNAVAILABLE",
} as const;

/** The API's code of an error (undefined when it's not one of the API's, e.g. offline). */
export const errorCodeOf = (error: unknown): string | undefined =>
  axios.isAxiosError(error) && typeof error.response?.data?.code === "string" ? error.response.data.code : undefined;

const base = (siteId: string) => `${constants.api}/sites/${encodeURIComponent(siteId)}/agenda`;

/** This browser's timezone ("America/Sao_Paulo"). */
export const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// The agenda's timezone of each Keepo, as read last: the editor's "today" is the agenda's
const knownTimezones: Record<string, string> = {};

/** The agenda's timezone, if read already. */
export const knownTimezone = (siteId: string): string | undefined => knownTimezones[siteId];

/** An agenda not saved yet starts in this browser's timezone (sent along). */
export const getAgenda = async (siteId: string): Promise<AgendaSettings> => {
  const settings: AgendaSettings = (await axios.get(base(siteId), { params: { timezone: browserTimezone() } })).data;
  if (settings?.timezone) {
    knownTimezones[siteId] = settings.timezone;
  }
  return settings;
};

/** Only what is sent changes. */
export const saveAgenda = async (siteId: string, settings: Partial<AgendaSettings>): Promise<AgendaSettings> => {
  const saved: AgendaSettings = (await axios.put(base(siteId), settings)).data;
  if (saved?.timezone) {
    knownTimezones[siteId] = saved.timezone;
  }
  return saved;
};

export type AgendaServiceList = { items: AgendaService[]; minSaleCents?: number };

/** The services and, with online payments on, the least a service can cost to be charged online. */
export const listServicesWithMinimum = async (siteId: string): Promise<AgendaServiceList> => {
  const { items, minSaleCents } = (await axios.get(`${base(siteId)}/services`)).data;
  return { items, minSaleCents };
};

export const listServices = async (siteId: string): Promise<AgendaService[]> =>
  (await listServicesWithMinimum(siteId)).items;

export const createService = async (siteId: string, service: AgendaServiceInput): Promise<AgendaService> =>
  (await axios.post(`${base(siteId)}/services`, service)).data;

/** Only what is sent changes. */
export const updateService = async (siteId: string, id: string, service: AgendaServiceInput): Promise<AgendaService> =>
  (await axios.put(`${base(siteId)}/services/${encodeURIComponent(id)}`, service)).data;

/** It leaves the agenda; the bookings keep what was booked. */
export const deleteService = async (siteId: string, id: string): Promise<void> => {
  await axios.delete(`${base(siteId)}/services/${encodeURIComponent(id)}`);
};

export const orderServices = async (siteId: string, ids: string[]): Promise<void> => {
  await axios.post(`${base(siteId)}/services/order`, { ids });
};

/** The bookings from `from` to `to` (YYYY-MM-DD, at most 62 days), in order, with the confirmed total. */
export const listBookings = async (siteId: string, from: string, to: string): Promise<AgendaBookingList> =>
  (await axios.get(`${base(siteId)}/bookings`, { params: { from, to } })).data;

/** A time of the owner's grid of a day: free, or held by a booking (the owner books it anyway, if they want). */
export type AgendaSlot = { time: string; busy: boolean };

/** The day's grid of times for a service (the agenda's hours; none on a day without them). */
export const listSlots = async (siteId: string, service: string, date: string): Promise<AgendaSlot[]> =>
  (await axios.get(`${base(siteId)}/slots`, { params: { service, date } })).data.slots;

export const createBooking = async (siteId: string, booking: AgendaBookingInput): Promise<AgendaBooking> =>
  (await axios.post(`${base(siteId)}/bookings`, booking)).data;

/** The owner changes a booking: service, day, time and the customer's data (any time; the customer hears of it). */
export const updateBooking = async (
  siteId: string,
  id: string,
  booking: Partial<AgendaBookingInput>
): Promise<AgendaBooking> => (await axios.put(`${base(siteId)}/bookings/${encodeURIComponent(id)}`, booking)).data;

const change = async (siteId: string, id: string, what: "confirm" | "decline" | "cancel"): Promise<AgendaBooking> =>
  (await axios.post(`${base(siteId)}/bookings/${encodeURIComponent(id)}/${what}`)).data;

export const confirmBooking = (siteId: string, id: string) => change(siteId, id, "confirm");
export const declineBooking = (siteId: string, id: string) => change(siteId, id, "decline");
export const cancelBooking = (siteId: string, id: string) => change(siteId, id, "cancel");

/** Gives the customer back all they paid, by the same method (the booking comes back canceled, its time free). */
export const refundBooking = async (siteId: string, id: string): Promise<AgendaBooking> =>
  (await axios.post(`${base(siteId)}/bookings/${encodeURIComponent(id)}/refund`)).data;
